/**
 * @fileoverview Monitoring Service for System Metrics and Health Tracking
 * 
 * This module provides application monitoring and metrics collection capabilities.
 * Records system metrics (counters, gauges, histograms), tracks system health,
 * and provides dashboard overview data for admin interfaces.
 * 
 * @module lib/monitoring
 * 
 * Key Features:
 * - Metric recording (counter, gauge, histogram types)
 * - System health checks via edge function
 * - Dashboard overview statistics (users, jobs, RFQs, health, errors)
 * - Performance tracking and error rate calculations
 * - Label-based metric filtering for multi-dimensional analysis
 * 
 * Common Use Cases:
 * 1. Page view tracking for analytics
 * 2. User action monitoring for engagement metrics
 * 3. Error rate tracking for system health
 * 4. Performance monitoring for slow operations
 * 5. Business metrics (jobs created, bids submitted, emails sent)
 * 
 * @example Page view tracking
 * ```typescript
 * import { monitoring } from '@/lib/monitoring';
 * 
 * useEffect(() => {
 *   monitoring.recordPageView('/jobs/create', user?.id);
 * }, []);
 * ```
 * 
 * @example Performance monitoring
 * ```typescript
 * const startTime = Date.now();
 * try {
 *   await expensiveOperation();
 *   await monitoring.recordPerformance(
 *     'expensiveOperation',
 *     Date.now() - startTime,
 *     true
 *   );
 * } catch (error) {
 *   await monitoring.recordPerformance(
 *     'expensiveOperation',
 *     Date.now() - startTime,
 *     false
 *   );
 * }
 * ```
 * 
 * @example Business metric
 * ```typescript
 * await monitoring.recordMetric(
 *   METRICS.JOB_CREATED,
 *   'counter',
 *   1,
 *   { category: job.category, organization_id: job.organization_id }
 * );
 * ```
 */

import { supabase } from '@/integrations/supabase/client'
import { invokeEdgeFunction } from '@/lib/edgeFunctionClient'

/**
 * System metric record from database
 * 
 * @property id - Unique metric ID
 * @property metric_name - Name identifier for the metric
 * @property metric_type - Type of metric (counter/gauge/histogram)
 * @property metric_value - Numeric value of the metric
 * @property labels - Arbitrary JSON labels for filtering and grouping
 * @property recorded_at - Timestamp when metric was recorded
 */
export interface SystemMetric {
  id: string
  metric_name: string
  metric_type: string
  metric_value: number
  labels: any
  recorded_at: string
}

/**
 * System health check result
 * 
 * @property status - Overall system health status
 * @property timestamp - When health check was performed
 * @property checks - Individual component health checks
 * @property version - Application version identifier
 */
export interface HealthCheck {
  status: 'healthy' | 'degraded' | 'unhealthy'
  timestamp: string
  checks: {
    name: string
    status: 'healthy' | 'degraded' | 'unhealthy'
    error?: string
    metrics?: Record<string, any>
    timestamp: string
  }[]
  version: string
}

/**
 * Monitoring service for metrics collection and system health tracking
 * 
 * @remarks
 * - Singleton instance exported as `monitoring`
 * - Stores metrics in `system_metrics` table
 * - Supports label-based filtering for multi-dimensional queries
 * - Does not throw on errors to prevent monitoring from breaking app
 */
class MonitoringService {
  /**
   * Records a metric value to the database
   * 
   * @param name - Metric name (use METRICS constants for consistency)
   * @param type - Metric type:
   *   - counter: Incrementing count (page views, events)
   *   - gauge: Point-in-time value (active users, memory usage)
   *   - histogram: Duration/distribution (request latency, file sizes)
   * @param value - Numeric metric value
   * @param labels - Optional labels for filtering (user_id, org_id, etc.)
   * @returns Promise<void>
   * 
   * @example Record job creation
   * ```typescript
   * await monitoring.recordMetric(
   *   'job_created',
   *   'counter',
   *   1,
   *   { category: 'print_materials', organization_id: 'org-123' }
   * );
   * ```
   * 
   * @example Record memory usage
   * ```typescript
   * await monitoring.recordMetric(
   *   'memory_usage_mb',
   *   'gauge',
   *   performance.memory.usedJSHeapSize / 1024 / 1024,
   *   { page: '/jobs' }
   * );
   * ```
   * 
   * @remarks
   * - Does not throw on error to prevent monitoring from breaking app
   * - Logs errors to console for debugging
   */
  async recordMetric(
    name: string,
    type: 'counter' | 'gauge' | 'histogram',
    value: number,
    labels: Record<string, any> = {}
  ): Promise<void> {
    try {
      await supabase
        .from('system_metrics')
        .insert({
          metric_name: name,
          metric_type: type,
          metric_value: value,
          labels
        })
    } catch (error) {
      console.error('Error recording metric:', error)
    }
  }

  /**
   * Retrieves metrics from database with optional filtering
   * 
   * @param metricName - Optional filter by specific metric name
   * @param startTime - Optional start time for time range filter
   * @param endTime - Optional end time for time range filter
   * @param limit - Maximum number of metrics to return (default: 100)
   * @returns Promise<SystemMetric[]> - Array of metrics, newest first
   * 
   * @example Get recent error metrics
   * ```typescript
   * const errorMetrics = await monitoring.getMetrics(
   *   'application_error',
   *   new Date(Date.now() - 24 * 60 * 60 * 1000), // Last 24 hours
   *   new Date()
   * );
   * ```
   * 
   * @example Get all recent metrics
   * ```typescript
   * const recentMetrics = await monitoring.getMetrics(
   *   undefined,
   *   new Date(Date.now() - 60 * 60 * 1000) // Last hour
   * );
   * ```
   */
  async getMetrics(
    metricName?: string,
    startTime?: Date,
    endTime?: Date,
    limit: number = 100
  ): Promise<SystemMetric[]> {
    try {
      let query = supabase
        .from('system_metrics')
        .select('*')
        .order('recorded_at', { ascending: false })
        .limit(limit)

      if (metricName) {
        query = query.eq('metric_name', metricName)
      }

      if (startTime) {
        query = query.gte('recorded_at', startTime.toISOString())
      }

      if (endTime) {
        query = query.lte('recorded_at', endTime.toISOString())
      }

      const { data, error } = await query

      if (error) throw error
      return data || []
    } catch (error) {
      console.error('Error fetching metrics:', error)
      return []
    }
  }

  /**
   * Retrieves current system health status from edge function
   * 
   * @returns Promise<HealthCheck | null> - Health check result or null on error
   * 
   * @example Display health status
   * ```typescript
   * const health = await monitoring.getHealthStatus();
   * if (health?.status === 'unhealthy') {
   *   showMaintenanceBanner();
   * }
   * ```
   * 
   * @remarks
   * - Calls 'health-check' edge function
   * - Returns null on error rather than throwing
   * - Health checks typically include: database, storage, email service
   */
  async getHealthStatus(): Promise<HealthCheck | null> {
    try {
      const response = await invokeEdgeFunction('health-check', {
        callerOperation: 'monitoring.healthCheck',
      })
      
      if (response.error) {
        console.error('Health check error:', response.error)
        return null
      }

      return response.data as HealthCheck
    } catch (error) {
      console.error('Error getting health status:', error)
      return null
    }
  }

  /**
   * Retrieves system overview statistics for admin dashboard
   * 
   * Aggregates key metrics for a high-level system overview display.
   * 
   * @returns Promise with system overview data
   * 
   * @example Admin dashboard
   * ```typescript
   * const overview = await monitoring.getSystemOverview();
   * console.log(`Active users (24h): ${overview.activeUsers}`);
   * console.log(`Total jobs: ${overview.totalJobs}`);
   * console.log(`System health: ${overview.systemHealth}`);
   * console.log(`Error rate: ${overview.errorRate.toFixed(2)} errors/min`);
   * ```
   * 
   * @remarks
   * - Active users: Unique users with audit log entries in last 24 hours
   * - Total jobs: Count from jobs table
   * - Active RFQs: RFQs with status 'open' or 'draft'
   * - System health: Based on most recent health check metric
   * - Error rate: From recent error metrics
   */
  async getSystemOverview(): Promise<{
    activeUsers: number
    totalJobs: number
    activeRFQs: number
    systemHealth: string
    errorRate: number
  }> {
    try {
      const { count: activeUsers } = await supabase
        .from('audit_logs')
        .select('user_id', { count: 'exact', head: true })
        .gte('created_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString())

      const { count: totalJobs } = await supabase
        .from('jobs')
        .select('*', { count: 'exact', head: true })

      const { count: activeRFQs } = await supabase
        .from('rfqs')
        .select('*', { count: 'exact', head: true })
        .in('status', ['open', 'draft'])

      const healthMetrics = await this.getMetrics('health_check', new Date(Date.now() - 60 * 60 * 1000))
      const recentHealth = healthMetrics[0]
      const systemHealth = recentHealth?.metric_value === 1 ? 'healthy' : 
                          recentHealth?.metric_value === 0.5 ? 'degraded' : 'unhealthy'

      const errorMetrics = await this.getMetrics('error_rate', new Date(Date.now() - 60 * 60 * 1000))
      const errorRate = errorMetrics[0]?.metric_value || 0

      return {
        activeUsers: activeUsers || 0,
        totalJobs: totalJobs || 0,
        activeRFQs: activeRFQs || 0,
        systemHealth,
        errorRate
      }
    } catch (error) {
      console.error('Error getting system overview:', error)
      return {
        activeUsers: 0,
        totalJobs: 0,
        activeRFQs: 0,
        systemHealth: 'unhealthy',
        errorRate: 0
      }
    }
  }

  /**
   * Records a page view event
   * 
   * @param page - Page route or identifier
   * @param userId - Optional user ID for user-specific analytics
   * @returns Promise<void>
   * 
   * @example Track page view
   * ```typescript
   * useEffect(() => {
   *   monitoring.recordPageView(window.location.pathname, user?.id);
   * }, [window.location.pathname]);
   * ```
   */
  async recordPageView(page: string, userId?: string): Promise<void> {
    await this.recordMetric('page_view', 'counter', 1, { page, user_id: userId })
  }

  /**
   * Records a user action event
   * 
   * @param action - Action identifier (e.g., 'submit_bid', 'create_job')
   * @param userId - Optional user ID
   * @param metadata - Additional action metadata
   * @returns Promise<void>
   * 
   * @example Track button click
   * ```typescript
   * const handleSubmit = async () => {
   *   await monitoring.recordUserAction('submit_bid', user?.id, { 
   *     rfq_id: rfqId,
   *     bid_amount: bidAmount
   *   });
   *   await submitBid();
   * };
   * ```
   */
  async recordUserAction(action: string, userId?: string, metadata: Record<string, any> = {}): Promise<void> {
    await this.recordMetric('user_action', 'counter', 1, { action, user_id: userId, ...metadata })
  }

  /**
   * Records an error event
   * 
   * @param error - Error message or identifier
   * @param severity - Error severity level
   * @returns Promise<void>
   * 
   * @example Track validation error
   * ```typescript
   * if (!isValid) {
   *   await monitoring.recordError('invalid_bid_amount', 'low');
   * }
   * ```
   */
  async recordError(error: string, severity: 'low' | 'medium' | 'high' | 'critical' = 'medium'): Promise<void> {
    await this.recordMetric('error', 'counter', 1, { error, severity })
  }

  /**
   * Records operation performance timing
   * 
   * @param operation - Operation identifier
   * @param duration - Duration in milliseconds
   * @param success - Whether operation succeeded
   * @returns Promise<void>
   * 
   * @example Track API call performance
   * ```typescript
   * const start = Date.now();
   * try {
   *   const result = await fetchData();
   *   await monitoring.recordPerformance('fetch_data', Date.now() - start, true);
   * } catch (error) {
   *   await monitoring.recordPerformance('fetch_data', Date.now() - start, false);
   * }
   * ```
   */
  async recordPerformance(operation: string, duration: number, success: boolean = true): Promise<void> {
    await this.recordMetric('performance', 'histogram', duration, { operation, success })
  }

  /**
   * Records application error from error tracking system
   * 
   * @param errorData - Error data object with severity, type, category
   * @returns Promise<void>
   * 
   * @remarks
   * Used internally by errorTracking service to record error metrics
   */
  async recordApplicationError(errorData: any): Promise<void> {
    await this.recordMetric('application_error', 'counter', 1, {
      severity: errorData.severity,
      type: errorData.error_type,
      category: errorData.error_category
    })
  }

  /**
   * Calculates error rate over a time window
   * 
   * @param timeWindow - Time window in milliseconds (default: 1 hour)
   * @returns Promise<number> - Errors per minute
   * 
   * @example Check error rate
   * ```typescript
   * const errorRate = await monitoring.getErrorRate(30 * 60 * 1000); // Last 30 min
   * if (errorRate > 10) {
   *   sendAlertToAdmin('High error rate detected');
   * }
   * ```
   */
  async getErrorRate(timeWindow: number = 3600000): Promise<number> {
    const errors = await this.getMetrics(
      'application_error',
      new Date(Date.now() - timeWindow)
    )
    return errors.length / (timeWindow / 1000 / 60) // errors per minute
  }

  /**
   * Gets count of unresolved critical errors
   * 
   * @returns Promise<number> - Count of critical errors not yet resolved
   * 
   * @example Show critical error badge
   * ```typescript
   * const criticalCount = await monitoring.getCriticalErrorCount();
   * if (criticalCount > 0) {
   *   showCriticalErrorBadge(criticalCount);
   * }
   * ```
   */
  async getCriticalErrorCount(): Promise<number> {
    try {
      const { count } = await supabase
        .from('application_errors')
        .select('*', { count: 'exact', head: true })
        .eq('severity', 'critical')
        .eq('resolved', false)
      return count || 0
    } catch (error) {
      console.error('Error getting critical error count:', error)
      return 0
    }
  }
}

/**
 * Singleton instance of MonitoringService
 * 
 * @example Basic usage
 * ```typescript
 * import { monitoring, METRICS } from '@/lib/monitoring';
 * 
 * await monitoring.recordMetric(METRICS.JOB_CREATED, 'counter', 1);
 * ```
 */
export const monitoring = new MonitoringService()

/**
 * Common metric names for consistency across the application
 * 
 * @remarks
 * Always use these constants rather than hardcoded strings for type safety
 */
export const METRICS = {
  PAGE_VIEW: 'page_view',
  USER_ACTION: 'user_action',
  ERROR: 'error',
  PERFORMANCE: 'performance',
  HEALTH_CHECK: 'health_check',
  EMAIL_SENT: 'email_sent',
  JOB_CREATED: 'job_created',
  BID_SUBMITTED: 'bid_submitted',
} as const
